import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useCreateScheduleMutation } from './__generated__/CreateScheduleModal.graphql';

const FormData = z.object({
  customerId: z.string(),
  title: z
    .string()
    .min(1)
    .regex(/.*\S.*/, t('titleSpaceWarn')),
  description: z.string().optional(),
});
type FormData = z.infer<typeof FormData>;

interface Props {
  customerId: string;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
}

export function CreateScheduleModal({ customerId, isOpen, onCancel, onSuccess }: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={onCancel}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <CreateScheduleModalContent
          customerId={customerId}
          initialFocusRef={initialFocusRef}
          onCancel={onCancel}
          onSuccess={onSuccess}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  customerId: string;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
};

function CreateScheduleModalContent({
  customerId,
  initialFocusRef,
  onCancel,
  onSuccess,
}: ContentProps) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormData>({
    defaultValues: { customerId, title: '' },
    mode: 'onChange',
    resolver: zodResolver(FormData),
  });
  const { ref: titleInputRef, ...titleInputProps } = register('title');

  const [createSchedule] = useCreateScheduleMutation();

  const onSubmit = useCallback(
    async ({ customerId, title, description }: FormData) => {
      try {
        const { data } = await createSchedule({
          variables: { input: { customerId, title, description: description ?? '' } },
        });

        if (!data || !data.scheduleCreate.schedule) {
          throw new Error('Create schedule failed');
        }

        await onSuccess?.(data.scheduleCreate.schedule.id);
      } catch (err) {
        toast({
          status: 'error',
          title: t('scheduleErr'),
          description: fromError(err, 'CreateSchedule', {
            SCHEDULE_LIMIT_REACHED: t('scheduleErrDesc'),
            WAVE_SUBSCRIPTION_SCHEDULES_LIMIT_REACHED: t('scheduleErrDesc2'),
            SCHEDULE_EMPTY_TITLE: t('scheduleErrDesc1'),
          }),
        });
      }
    },
    [toast, createSchedule, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>{t('newSchedule')}</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <VStack>
          <FormControl isRequired isInvalid={Boolean(errors.title)} marginBottom={5}>
            <FormLabel>{t('scheduleTitle')}</FormLabel>
            <Input
              ref={(r) => {
                titleInputRef(r);
                initialFocusRef.current = r;
              }}
              {...titleInputProps}
            />
            <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.description)}>
            <FormLabel>{t('description')}</FormLabel>
            <Textarea {...register('description')} background="white" borderRadius="base" />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={!isDirty || isSubmitting || Object.keys(errors).length > 0}
          isLoading={isSubmitting}
        >
          {t('createSchedule')}
        </Button>
      </ModalFooter>
    </form>
  );
}

CreateScheduleModal.graphql = {
  mutations: {
    CreateSchedule: gql`
      mutation CreateSchedule($input: ScheduleCreateInput!) {
        scheduleCreate(input: $input) {
          schedule {
            id
            ...ScheduleTable_schedule
          }
        }
      }
    `,
  },
};
